import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@fluentui/react-components';
import { ShellIcons, StatusDot, complianceTokenFor, shellMedia, shellMetrics, tokens, type ComplianceTone } from '@vigeo/ui';
import {
  AGENCY_TIME_ZONE,
  candidateStagesByPerson,
  daysUntilDeadline,
  isComplianceTracked,
  type Dashboard,
  type OfferRow,
  type Person,
  type ReadinessState,
  type UnreadableOfferRow,
} from '@vigeo/shared';
import { type PeopleReadiness } from '@/features/candidates';
import { ReadinessBadge, formatDue, readinessBandOrder } from '@/features/readiness';
import { CardEmpty, CardRow, CardRows, DashboardCardShell, RowAction, ToneBadge } from './chrome.js';
import { buildHiring, unplacedReason } from './hiring.js';

/**
 * THE DASHBOARD IS A LIST OF CARDS, NOT A FIXED PAGE.
 *
 * Julio's instruction for 06b 0.1 is that this region stay flexible: what it shows can change at
 * any point. Each card registers here under a key and the page renders the ROWS OF KEYS the layout
 * names, so changing what HR sees is changing that layout rather than rewriting a page.
 *
 * Every card takes the same single prop — the whole of what the page has fetched — so the page can
 * render any of them without knowing which it has. A card needing something that prop does not
 * carry is a signal to extend it, never to reach around it with its own request.
 *
 * NOTHING HERE COUNTS ANYTHING THE APP DOES NOT ALREADY COUNT. Every figure on this page is either
 * a field of `/api/dashboard`, a row of `/api/offers`, or a fold over checklists the Person page
 * and the Roster already derive. No new stored counter, and no second definition of a bucket.
 */
export interface DashboardCardProps {
  readonly data: Dashboard;
  /** `/api/offers`' rows. Undefined while the request is in flight or has failed. */
  readonly offers: readonly OfferRow[] | undefined;
  /**
   * `/api/offers`' `unavailable` — candidates whose stored record could not be read (#299).
   *
   * REQUIRED, not optional, and passed alongside `offers` everywhere it is passed (#327). A caller
   * that forgets it drops those people out of the Hiring card's account of the pipeline silently,
   * which is the whole defect this field exists to close; making it optional would let the next
   * caller reintroduce it by omission.
   */
  readonly unreadable: readonly UnreadableOfferRow[] | undefined;
  readonly people: readonly Person[];
  /** One derived checklist per person, from the query the Person page and Roster share. */
  readonly readiness: PeopleReadiness;
  /** Job-profile keys to a label, from the catalog. '' while the catalog is still loading. */
  readonly roleOf: (keys: readonly string[]) => string;
}

const useStyles = makeStyles({
  /** The frame's five stage tiles (`288:224`): equal columns, 8px apart, wrapping before they squash. */
  stages: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalS,
    alignItems: 'stretch',
    [shellMedia.belowTablet]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    padding: tokens.spacingHorizontalM,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  stageTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  /** HR/Title 2 — 24/32 semibold, tabular so five counts line up across the row. */
  stageCount: { fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold, fontFamily: tokens.fontFamilyNumeric, color: tokens.colorNeutralForeground1 },
  stageLabel: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  stageCaption: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground2 },
  stageNames: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    paddingTop: tokens.spacingVerticalXS,
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
  },
  stageName: { overflowWrap: 'anywhere' },
  /** The 48px weekday block beside an event (`288:188`). */
  date: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: '3rem',
    paddingBlock: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  dateWeekday: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground2 },
  dateDay: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, color: tokens.colorNeutralForeground1 },
  /**
   * The frame draws a person's name in the brand colour on the expiry rows — it is a link.
   *
   * On a phone it is also a TOUCH TARGET, so it carries the 44px minimum every other link on this
   * page does (Gate 6). Text at 14/20 is 17px tall, which is what the browser sweep measured before
   * this was here.
   */
  personLink: {
    color: tokens.colorBrandForegroundLink,
    fontWeight: tokens.fontWeightSemibold,
    display: 'inline-flex',
    alignItems: 'center',
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  count: { fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold, fontFamily: tokens.fontFamilyNumeric, flexShrink: 0, color: tokens.colorNeutralForeground1 },
  note: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
});

/* ---- Waiting on HR (`288:130`) --------------------------------------------------------- */

interface WaitingRow {
  readonly key: string;
  readonly count: number;
  readonly title: string;
  readonly detail: string;
  readonly icon: () => ReactNode;
  readonly tone?: ComplianceTone;
  readonly action?: { readonly label: string; readonly to: string };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * What only HR can move, MOST URGENT FIRST — the card's own subtitle, so the order is the content.
 *
 * Urgency is the array order and nothing else: a delivery that failed outranks a queue that is
 * merely waiting, and both outrank an offer nobody has written. A row whose count is zero is
 * DROPPED rather than drawn as "0 submissions to review": a permanent zero teaches HR to skip the
 * line on the day it stops being zero, which is the one day it matters.
 */
function waitingRows(props: DashboardCardProps): readonly WaitingRow[] {
  const failed = props.data.outstanding.communications.failed;
  const review = props.data.review.pending;
  /**
   * Read from the offer ROWS, not from a count of our own — `candidateStagesByPerson` folds
   * `offerGroups` the same way the Hiring card below does, so this figure and that card's first
   * stage cannot disagree, and neither can disagree with the Offers list.
   *
   * PEOPLE, from rows that are one per OFFER (#297). Filtering the rows would count a candidate with
   * two offers twice and tell HR there are more offers to write than there are candidates without
   * one. The fold is shared rather than repeated here for exactly that reason.
   *
   * IT COUNTS CANDIDATES THE OFFERS LIST SPOKE FOR, which is every candidate it could read. One it
   * could not is absent from this line as well as from the Hiring stages, and belongs in that card's
   * `unplaced` rather than being guessed at here.
   */
  const toPrepare = [...candidateStagesByPerson(props.offers ?? []).values()].filter((stage) => stage === 'prepare-offer').length;
  const rows: readonly WaitingRow[] = [
    {
      key: 'email-failed',
      count: failed,
      title: `${plural(failed, 'email', 'emails')} couldn’t be sent`,
      detail: 'Nobody received them. Re-send from the person’s record.',
      icon: () => <ShellIcons.alert />,
      tone: 'critical',
    },
    {
      key: 'review',
      count: review,
      title: `${plural(review, 'submission', 'submissions')} to review`,
      detail: 'Waiting for an HR decision.',
      icon: () => <ShellIcons.review />,
      action: { label: 'Open Review', to: '/app/review' },
    },
    {
      key: 'offers',
      count: toPrepare,
      title: `${plural(toPrepare, 'offer', 'offers')} to prepare`,
      detail: 'Interviewed, with no offer out yet.',
      icon: () => <ShellIcons.briefcase />,
      action: { label: 'Open Offers', to: '/app/offers' },
    },
  ];
  return rows.filter((row) => row.count > 0);
}

function WaitingOnHrCard(props: DashboardCardProps) {
  const rows = waitingRows(props);
  return (
    <DashboardCardShell id="dashboard-waiting" title="Waiting on HR" caption="Things only HR can move forward, most urgent first.">
      {rows.length === 0 ? (
        <CardEmpty>Nothing is waiting on HR.</CardEmpty>
      ) : (
        <CardRows label="Waiting on HR">
          {rows.map((row) => (
            <CardRow
              key={row.key}
              density="roomy"
              icon={{ render: row.icon, ...(row.tone === undefined ? {} : { tone: row.tone }) }}
              title={row.title}
              detail={row.detail}
              trailing={row.action === undefined ? null : <RowAction label={row.action.label} to={row.action.to} />}
            />
          ))}
        </CardRows>
      )}
    </DashboardCardShell>
  );
}

/* ---- Overdue updates (`609:26466`) ------------------------------------------------------ */

/**
 * Steps HR sent back that did not come back in time.
 *
 * WHY IT IS THE FIRST CARD and why it disappears: it is the only card on the page about work that
 * has already slipped, so the frame puts it above everything; and an "Overdue updates · 0" card
 * sitting in red at the top of a healthy dashboard every morning is how a warning stops being
 * read. When nothing is late the page leaves it out entirely — see `DASHBOARD_CARD_HIDDEN`.
 *
 * "Only HR sees this" is about the CANDIDATE, not about the two HR roles: the portal has no
 * dashboard at all, and both an admin and a viewer may already read every one of these steps on
 * the person's own record. Nothing here is a control, so a viewer sees exactly what an admin does.
 */
function OverdueCard({ data }: DashboardCardProps) {
  const styles = useStyles();
  const { rows, total } = data.overdue;
  return (
    <DashboardCardShell
      id="dashboard-overdue"
      tone="critical"
      /**
       * THE TOTAL, not `rows.length`. The rows are capped so the card stays a prompt rather than a
       * worklist, so with twenty overdue the heading would otherwise read "· 8" — and eight is the
       * number HR would act on.
       */
      title={`Overdue updates · ${total}`}
      caption="Steps you sent back that weren’t sent by their due date. Only HR sees this. “Open step” goes to that step in the candidate’s record."
    >
      <CardRows label="Overdue updates">
        {rows.map((row) => (
          <CardRow
            key={row.instanceId}
            density="roomy"
            title={`${row.personName} · ${row.step}`}
            detail={`Sent back ${formatDue(row.sentBackAt)} · due ${formatDue(row.dueAt)}`}
            trailing={(
              <>
                <ToneBadge tone="critical">{plural(row.daysOverdue, 'day', 'days')} overdue</ToneBadge>
                {/*
                  * The frame's annotation asks for the candidate's record › Documents & checklist ›
                  * this step. The first two exist as a route; there is no per-step anchor yet, so
                  * this opens the section that holds it and stops there rather than inventing one.
                  */}
                <Link to={`/app/people/${row.personId}?section=documents`} className={styles.personLink}>Open step</Link>
              </>
            )}
          />
        ))}
      </CardRows>
      {total > rows.length ? (
        <span className={styles.note}>
          Showing the {rows.length} most overdue. Open a person’s record for the rest.
        </span>
      ) : null}
    </DashboardCardShell>
  );
}

/* ---- Coming up this week (`288:178`) --------------------------------------------------- */

/**
 * The agency's day, never the browser's.
 *
 * An orientation at 9am in the agency's zone must read as 9am to an HR admin working from
 * anywhere, and near midnight the browser's own date is simply the wrong one — the same reason
 * the page's date line is formatted this way.
 */
const inAgencyZone = (instant: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { ...options, timeZone: AGENCY_TIME_ZONE }).format(new Date(instant));

function ComingUpCard({ data }: DashboardCardProps) {
  const styles = useStyles();
  return (
    <DashboardCardShell id="dashboard-upcoming" title="Coming up this week">
      {data.upcoming.events.length === 0 ? (
        <CardEmpty>Nothing is scheduled yet.</CardEmpty>
      ) : (
        <CardRows label="Coming up">
          {data.upcoming.events.map((event) => (
            <CardRow
              key={event.recordId}
              lead={(
                <span className={styles.date}>
                  {/* Uppercased in CONTENT, not in CSS: a screen reader should hear "Tue", and
                      `text-transform` would leave the accessible name reading "TUE". */}
                  <span className={styles.dateWeekday}>{inAgencyZone(event.startsAt, { weekday: 'short' }).toUpperCase()}</span>
                  <span className={styles.dateDay}>{inAgencyZone(event.startsAt, { day: 'numeric' })}</span>
                </span>
              )}
              title={event.title}
              detail={`${event.personName} · ${inAgencyZone(event.startsAt, { hour: 'numeric', minute: '2-digit' })}`}
            />
          ))}
        </CardRows>
      )}
    </DashboardCardShell>
  );
}

/* ---- Hiring (`288:215`) ---------------------------------------------------------------- */

function HiringCard(props: DashboardCardProps) {
  const styles = useStyles();
  const onboarding = props.people.filter((person) => person.lifecycle === 'onboarding');
  const hiring = buildHiring({
    offers: props.offers,
    unreadable: props.unreadable,
    onboarding,
    checklistOf: (id) => {
      const entry = props.readiness.entryOf(id);
      return entry.status === 'ready' ? entry.checklist : null;
    },
    roleOf: props.roleOf,
  });

  return (
    <DashboardCardShell
      id="dashboard-hiring"
      title="Hiring"
      caption="Everyone not yet Active, in the order HR works them."
      action={{ label: 'Open Candidates', to: '/app/people' }}
    >
      <div className={styles.stages}>
        {hiring.stages.map(({ stage, presenter, count, people, more }) => (
          <section key={stage} className={styles.stage} aria-label={presenter.label}>
            <div className={styles.stageTop}>
              <span className={styles.stageCount}>{count}</span>
              {/* The lifecycle, from the registry that names it everywhere else — so the badge
                  cannot read "Candidate" here and "In the pipeline" on the board. */}
              <StatusDot color={presenter.lifecycle === 'onboarding' ? complianceTokenFor.good : tokens.colorNeutralForeground3}>
                {presenter.lifecycle === 'onboarding' ? 'Onboarding' : 'Candidate'}
              </StatusDot>
            </div>
            <span className={styles.stageLabel}>{presenter.label}</span>
            <span className={styles.stageCaption}>{presenter.caption}</span>
            {people.length === 0 ? null : (
              <ul className={styles.stageNames} aria-label={`${presenter.label}: who is here`}>
                {people.map((person) => (
                  <li key={person.personId} className={styles.stageName}>
                    {person.role === '' ? person.name : `${person.name} · ${person.role}`}
                  </li>
                ))}
                {more === 0 ? null : <li className={styles.stageName}>+{more} more</li>}
              </ul>
            )}
          </section>
        ))}
      </div>
      {/* Stated, never swallowed: five confident figures that sum to less than the pipeline would
          hide a person nobody is working. */}
      {hiring.unplaced > 0 ? (
        <span className={styles.note}>
          {/* The REASON is `unplacedReason`'s, not this line's: `unplaced` now has two sources and
              this sentence used to assert the first one unconditionally (#327). A degraded
              candidate's checklist may well have been derived — nobody knows, because their record
              could not be read. */}
          {plural(hiring.unplaced, 'person is', 'people are')} not placed yet — {unplacedReason(hiring)}.
        </span>
      ) : null}
    </DashboardCardShell>
  );
}

/* ---- Roster (`288:278`) ---------------------------------------------------------------- */

/**
 * What each band MEANS, in the frame's words (`290:431`).
 *
 * `satisfies Record<ReadinessState, string>` for the same reason the presenter registries are:
 * a sixth state is a compile error here rather than a band with a blank second line.
 * `setup_required` is not drawn in the frame — it is banded anyway (see `readinessBandOrder`), so
 * its line is written in the same voice as the four that are.
 */
const bandCaptions = {
  ready: 'Can be assigned',
  ready_with_exceptions: 'Assignable under a recorded, time-boxed exception',
  not_ready: 'Something is missing, and we know who owes it',
  needs_review: 'HR has to look before anything moves',
  setup_required: 'No job profile is set, so there is nothing to assess',
} as const satisfies Record<ReadinessState, string>;

function RosterCard({ people, readiness }: DashboardCardProps) {
  const styles = useStyles();
  const employed = people.filter((person) => person.lifecycle === 'active' || person.lifecycle === 'inactive');
  const active = employed.filter((person) => person.lifecycle === 'active').length;
  // Staff added by hand are in the headcount but in no readiness band: their compliance is not tracked yet (ADR-016 §4).
  const tracked = employed.filter(isComplianceTracked);
  const untracked = employed.length - tracked.length;

  /**
   * Banded only from checklists that have arrived. A band showing 0 while its checklists are still
   * resolving reads as a fact ("nobody needs review") rather than as "not known yet", and that is
   * the figure an HR admin would act on. The remainder is counted and said out loud.
   */
  const tally = new Map<ReadinessState, number>();
  let unresolved = 0;
  for (const person of tracked) {
    const state = readiness.stateOf(person.id);
    if (state === null) { unresolved += 1; continue; }
    tally.set(state, (tally.get(state) ?? 0) + 1);
  }

  return (
    <DashboardCardShell
      id="dashboard-roster"
      title="Roster"
      caption={`${plural(active, 'active employee', 'active employees')} · ${employed.length - active} inactive${untracked > 0 ? ` · ${untracked} not tracked yet` : ''}`}
      action={{ label: 'Open Roster', to: '/app/roster' }}
    >
      <CardRows label="Roster readiness">
        {readinessBandOrder.map((state) => (
          <CardRow
            key={state}
            title={<ReadinessBadge state={state} />}
            detail={bandCaptions[state]}
            trailing={<span className={styles.count}>{tally.get(state) ?? 0}</span>}
          />
        ))}
      </CardRows>
      {unresolved > 0 ? (
        <span className={styles.note}>
          {plural(unresolved, 'person is', 'people are')} not banded yet — their checklist has not been derived.
        </span>
      ) : null}
    </DashboardCardShell>
  );
}

/* ---- Credential expiry (`288:309`) ----------------------------------------------------- */

/**
 * Why the card says nothing is due. "None on file" and "none due" are different facts, and one
 * reassuring sentence covering both would tell HR that a query returning nothing is fine.
 */
function expiryCaption(data: Dashboard): string {
  if (data.expiry.counted === 0 && data.expiry.unassessable === 0) return 'No credential with an expiry date is on file yet.';
  return 'No credential is expired or inside its renewal window.';
}

function ExpiryCard({ data }: DashboardCardProps) {
  const styles = useStyles();
  const { expired, expiringSoon, soonest, unassessable } = data.expiry;
  return (
    <DashboardCardShell
      id="dashboard-expiry"
      title="Credential expiry"
      caption={`${expired} expired · ${expiringSoon} inside their renewal window${unassessable > 0 ? ` · ${unassessable} could not be assessed` : ''}`}
    >
      {soonest.length === 0 ? (
        <CardEmpty>{expiryCaption(data)}</CardEmpty>
      ) : (
        <CardRows label="Expiring soonest">
          {soonest.map((row) => {
            /**
             * Counted against `computedAt`, the one instant the whole response was built at —
             * never a clock read here, which would let the countdown and the figures above it
             * disagree by a day at midnight.
             */
            const days = daysUntilDeadline(row.expiresAt, data.computedAt);
            return (
              <CardRow
                key={`${row.personId}:${row.requirement}`}
                title={<Link to={`/app/people/${row.personId}`} className={styles.personLink}>{row.personName}</Link>}
                detail={`${row.requirement} · ${row.expired ? 'expired' : 'expires'} ${formatDue(row.expiresAt)}`}
                trailing={(
                  <ToneBadge tone={row.expired ? 'critical' : 'warning'}>
                    {row.expired ? 'Expired' : `In ${plural(days, 'day', 'days')}`}
                  </ToneBadge>
                )}
              />
            );
          })}
        </CardRows>
      )}
    </DashboardCardShell>
  );
}

/** Every card the dashboard knows how to draw, by key. */
export const DASHBOARD_CARDS: Readonly<Record<string, ComponentType<DashboardCardProps>>> = {
  overdue: OverdueCard,
  'waiting-on-hr': WaitingOnHrCard,
  'coming-up': ComingUpCard,
  hiring: HiringCard,
  roster: RosterCard,
  expiry: ExpiryCard,
};

/**
 * Cards that are a fixed-width ASIDE rather than a card that grows.
 *
 * Frame 0.1's work row is not two equal columns: "Waiting on HR" takes the remaining width and
 * "Coming up this week" is pinned at 376px (`288:178`). Recorded per card rather than per position,
 * so reordering a row cannot move the narrow column onto the wrong card.
 */
export const DASHBOARD_ASIDES: ReadonlySet<string> = new Set(['coming-up']);

/**
 * Cards that are left OUT of the page when they would have nothing to say.
 *
 * A card deciding for itself and returning null is not enough: the page would still draw its
 * wrapper and, for a card alone on a row, a row's worth of gap — a blank band at the top of the
 * dashboard every day nothing is overdue. So the page asks BEFORE it renders, and a hidden card
 * costs no element at all.
 *
 * Only "Overdue updates" is listed, and deliberately. Every other card on this page reports a
 * steady fact — a roster, a pipeline, a queue — and its empty state ("Nothing is waiting on HR")
 * is information. This one reports an exception, and an exception card showing zero is noise.
 */
export const DASHBOARD_CARD_HIDDEN: Readonly<Record<string, (props: DashboardCardProps) => boolean>> = {
  overdue: (props) => props.data.overdue.rows.length === 0,
};

/**
 * What the dashboard shows, in the frame's ROWS.
 *
 * Rows, not a flat list, because the frame's composition is rows: a wide card beside a narrow one,
 * a full-width card, then two equal cards (`288:129`, `288:215`, `288:277`). A flat list could not
 * say that, and the previous three-up grid said something the frame does not.
 *
 * Nothing here is stored yet. When the layout needs to change without a deploy — which matters,
 * because deploys are manual while CI billing is off — this becomes the fallback for a value read
 * from settings, and `DashboardPage` already accepts it as a prop.
 */
export const DEFAULT_DASHBOARD_LAYOUT: readonly (readonly string[])[] = [
  ['overdue'],
  ['waiting-on-hr', 'coming-up'],
  ['hiring'],
  ['roster', 'expiry'],
];
